import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Link,
  Tooltip,
  Divider,
  useMediaQuery,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { iresoftColors } from "../../../theme/ireColors";
import { Trans, useTranslation } from "react-i18next";

const QuoteWithArrow: React.FC = () => {
  const quoteRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const handleScrollToQuote = () => {
    if (!isVisible) {
      quoteRef.current?.scrollIntoView({ behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      document.documentElement.scrollTo({ top: 0, behavior: "smooth" });
      document.body.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const isSmallScreen = useMediaQuery("(max-width: 450px)");
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!quoteRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    observer.observe(quoteRef.current);

    return () => observer.disconnect();
  }, []);

  const QuoteDivider: React.FC = () => (
    <Divider
      sx={{
        height: 2,
        width: "75%",
        mx: "auto",
        my: 2,
        border: "none",
        background: `linear-gradient(to right, transparent, ${iresoftColors.iresoftRed}, transparent)`,
      }}
    />
  );

  return (
    <Box
      width="100%"
      p={2}
      display="flex"
      flexDirection="column"
      alignItems="center"
      mb={3}
    >
      <Tooltip title={t("pages.welcome.quote.tooltip")}>
        <IconButton
          onClick={handleScrollToQuote}
          sx={{
            color: iresoftColors.iresoftRed,
            mb: 2,
            mt: isSmallScreen ? -7 : -3,
            fontSize: 40,
            transition: "transform 0.3s",
            transform: isVisible ? "rotate(180deg)" : "rotate(0deg)",
          }}
        >
          <KeyboardArrowDownIcon fontSize="large" />
        </IconButton>
      </Tooltip>

      <Box
        ref={quoteRef}
        sx={{
          maxWidth: 1200,
          mx: "auto",
          my: 3,
          mb: 3,
          p: 4,
          borderRadius: 3,
          bgcolor: "background.paper",
          boxShadow: 3,
          textAlign: "center",
          position: "relative",
          "&::before": {
            content: '"“"',
            fontSize: 50,
            position: "absolute",
            top: -10,
            left: 10,
            color: iresoftColors.black40,
          },
          "&::after": {
            content: '"”"',
            fontSize: 50,
            position: "absolute",
            bottom: -30,
            right: 10,
            color: iresoftColors.black40,
          },
        }}
      >
        <Typography variant="h4" sx={{ mt: 2 }}>
          <Trans
            i18nKey="pages.welcome.quote.text.about"
            values={{ hours: "#2000 hodin" }}
            components={{
              iresoftLink: (
                <Link
                  href="https://www.iresoft.cz"
                  target="_blank"
                  rel="noopener"
                  sx={{
                    color: iresoftColors.iresoftRed,
                    textDecoration: "none",
                  }}
                />
              ),
              hashtag: (
                <span
                  style={{
                    color: iresoftColors.iresoftRed,
                    fontWeight: 600,
                  }}
                />
              ),
              ireRed: <span style={{ color: iresoftColors.iresoftRed }} />,
            }}
          />
        </Typography>
        <QuoteDivider />
        <Typography variant="h4" sx={{ lineHeight: 1.6, mt: 1 }}>
          <Trans
            i18nKey="pages.welcome.quote.text.intro"
            components={{
              irebook: <span style={{ color: iresoftColors.iresoftRed }} />,
              iresoft: <span style={{ color: iresoftColors.iresoftRed }} />,
            }}
          />
        </Typography>
        <QuoteDivider />
        <Typography variant="body1" sx={{ mt: 2 }}>
          <Trans
            i18nKey="pages.welcome.quote.text.thanks"
            components={{
              bold: <strong />,
              italic: <em />,
              br: <br />,
            }}
          />
        </Typography>
        <QuoteDivider />
        <Typography variant="body1" sx={{ mt: 1 }}>
          <Trans
            i18nKey="pages.welcome.quote.text.qr"
            components={{
              irebook: <span style={{ color: iresoftColors.iresoftRed }} />,
              br: <br />,
            }}
          />
          <Link
            href="https://www.iresoft.cz/irebook"
            target="_blank"
            rel="noopener"
            sx={{
              color: iresoftColors.iresoftRed,
              textDecoration: "none",
            }}
          >
            iresoft.cz/irebook
          </Link>
          .
        </Typography>
      </Box>
    </Box>
  );
};

export default QuoteWithArrow;
